import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload, FileText, Trash2, Eye, Loader2, BarChart2 } from 'lucide-react'
import { documentsApi, type Document } from '@/api/documents'
import { extractionApi } from '@/api/extraction'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatDate, formatRelativeDate, formatFileSize, statusColor } from '@/lib/utils'
import { ProcessingModal } from '@/components/extraction/ProcessingProgress'
import DocumentViewerDialog from '@/routes/projects/DocumentViewer'

interface Props {
  projectId: string
  onViewResults?: (documentId: string) => void
}

function extractionStatusColor(status: string | null): string {
  if (!status) return 'text-gray-500 bg-gray-50'
  return statusColor(status)
}

function confidenceColor(confidence: number | null): string {
  if (confidence === null) return 'text-gray-500 bg-gray-50'
  if (confidence >= 0.8) return 'text-green-600 bg-green-50'
  if (confidence >= 0.5) return 'text-yellow-600 bg-yellow-50'
  return 'text-red-600 bg-red-50'
}

export default function DocumentUpload({ projectId, onViewResults }: Props) {
  const queryClient = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 })
  const [uploadErrors, setUploadErrors] = useState<string[]>([])
  const [selectedDocs, setSelectedDocs] = useState<string[]>([])
  const [showProcessingModal, setShowProcessingModal] = useState(false)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [viewerDocumentId, setViewerDocumentId] = useState<string | null>(null)
  const [pageSize, setPageSize] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['documents', projectId],
    queryFn: () => documentsApi.list(projectId),
    refetchInterval: 5000,
  })

  const deleteMutation = useMutation({
    mutationFn: documentsApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents', projectId] }),
  })

  const [bulkDeleting, setBulkDeleting] = useState(false)
  const handleBulkDelete = async () => {
    if (selectedDocs.length === 0) return
    const n = selectedDocs.length
    if (!confirm(`Delete ${n} selected document${n !== 1 ? 's' : ''}? This cannot be undone.`)) return
    setBulkDeleting(true)
    try {
      // Run with limited concurrency (4 at a time) to avoid hammering the API
      const ids = [...selectedDocs]
      const concurrency = 4
      let i = 0
      await Promise.all(
        Array.from({ length: concurrency }, async () => {
          while (i < ids.length) {
            const id = ids[i++]
            try { await documentsApi.delete(id) } catch { /* keep going */ }
          }
        })
      )
      setSelectedDocs([])
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
    } finally {
      setBulkDeleting(false)
    }
  }

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const files = [...acceptedFiles]
      setUploading(true)
      setUploadErrors([])
      setUploadProgress({ done: 0, total: files.length })
      const errors: string[] = []

      // Uploads are now fast (parsing is async on the server), but we still
      // limit concurrency to 4 so we don't saturate the network on big batches.
      const concurrency = 4
      let idx = 0
      let completed = 0
      await Promise.all(
        Array.from({ length: Math.min(concurrency, files.length) }, async () => {
          while (idx < files.length) {
            const file = files[idx++]
            try {
              await documentsApi.upload(projectId, file)
            } catch (e: any) {
              const detail = e?.response?.data?.detail
              errors.push(`Failed to upload ${file.name}${detail ? `: ${detail}` : ''}`)
            } finally {
              completed += 1
              setUploadProgress({ done: completed, total: files.length })
              // Stream new docs into the list as they land
              queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
            }
          }
        })
      )

      setUploadErrors(errors)
      setUploading(false)
      // Leave the progress bar at 100% briefly then clear
      setTimeout(() => setUploadProgress({ done: 0, total: 0 }), 1500)
    },
    [projectId, queryClient]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
    },
    multiple: true,
  })

  const toggleSelect = (id: string) => {
    setSelectedDocs((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    )
  }

  const selectAll = () => {
    setSelectedDocs(documents.map((d) => d.id))
  }

  const handleProcess = () => {
    if (selectedDocs.length === 0) return
    setShowProcessingModal(true)
  }

  const handleProcessingClose = () => {
    setShowProcessingModal(false)
    // Invalidate queries so results and documents refresh
    queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
    queryClient.invalidateQueries({ queryKey: ['results', projectId] })
    queryClient.invalidateQueries({ queryKey: ['extraction-jobs', projectId] })
    queryClient.invalidateQueries({ queryKey: ['results-summary', projectId] })
  }

  return (
    <div className="space-y-6">
      {/* Upload zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50'
        }`}
      >
        <input {...getInputProps()} />
        {uploading || uploadProgress.total > 0 ? (
          <div className="flex flex-col items-center gap-3 w-full max-w-md mx-auto">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <p className="text-sm font-medium">
              {uploading
                ? `Uploading ${uploadProgress.done} of ${uploadProgress.total}…`
                : `Uploaded ${uploadProgress.done} of ${uploadProgress.total}`}
            </p>
            <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-[width] duration-300"
                style={{
                  width: `${uploadProgress.total > 0 ? (uploadProgress.done / uploadProgress.total) * 100 : 0}%`,
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Parsing continues in the background — status updates appear in the table below.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">
              {isDragActive ? 'Drop files here' : 'Drag & drop files, or click to browse'}
            </p>
            <p className="text-xs text-muted-foreground">PDF, DOC, DOCX up to 100MB each</p>
          </div>
        )}
      </div>

      {uploadErrors.length > 0 && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {uploadErrors.map((e, i) => <p key={i}>{e}</p>)}
        </div>
      )}

      {/* Stuck-document banner: shows when pending/processing/failed docs may need a kick */}
      <StuckDocsBanner documents={documents} projectId={projectId} queryClient={queryClient} />

      {/* Document list */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No documents yet. Upload some files to get started.
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDocs([])}
                disabled={selectedDocs.length === 0}
              >
                Clear
              </Button>
              <span className="text-sm text-muted-foreground">
                {selectedDocs.length} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkDelete}
                disabled={selectedDocs.length === 0 || bulkDeleting}
                className="text-destructive hover:bg-destructive/10 border-destructive/30 hover:text-destructive"
              >
                {bulkDeleting ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Deleting...</>
                ) : (
                  <><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete {selectedDocs.length > 0 ? `(${selectedDocs.length})` : ''}</>
                )}
              </Button>
              <Button
                size="sm"
                onClick={handleProcess}
                disabled={selectedDocs.length === 0}
              >
                Process {selectedDocs.length > 0 ? `(${selectedDocs.length})` : ''}
              </Button>
            </div>
          </div>

          {/* Documents table */}
          {(() => {
            const totalDocs = documents.length
            const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize))
            const safePage = Math.min(currentPage, totalPages)
            const start = (safePage - 1) * pageSize
            const end = Math.min(start + pageSize, totalDocs)
            const paginatedDocs = documents.slice(start, end)

            return (<>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium w-10">
                    <input
                      type="checkbox"
                      checked={selectedDocs.length === documents.length && documents.length > 0}
                      onChange={() => {
                        if (selectedDocs.length === documents.length) {
                          setSelectedDocs([])
                        } else {
                          selectAll()
                        }
                      }}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="text-left p-3 font-medium">Filename</th>
                  <th className="text-left p-3 font-medium">Type</th>
                  <th className="text-left p-3 font-medium">Pages</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Uploaded</th>
                  <th className="text-left p-3 font-medium">Last Run</th>
                  <th className="text-left p-3 font-medium">Extraction Status</th>
                  <th className="text-left p-3 font-medium">Avg Confidence</th>
                  <th className="text-left p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDocs.map((doc) => (
                  <tr
                    key={doc.id}
                    className={`border-t hover:bg-muted/20 cursor-pointer ${
                      selectedDocs.includes(doc.id) ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => toggleSelect(doc.id)}
                  >
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedDocs.includes(doc.id)}
                        onChange={() => toggleSelect(doc.id)}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="font-medium truncate max-w-xs" title={doc.original_filename}>
                          {doc.original_filename}
                        </span>
                        {doc.annotation_count > 0 && (
                          <Badge variant="outline" className="text-xs py-0 px-1.5 shrink-0 text-blue-600 border-blue-300 bg-blue-50">
                            {doc.annotation_count} ann.
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="text-xs uppercase">
                        {doc.file_type}
                      </Badge>
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {doc.page_count ?? '--'}
                    </td>
                    <td className="p-3">
                      <DocStatusBadge doc={doc} />
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {formatDate(doc.uploaded_at)}
                    </td>
                    <td className="p-3 text-muted-foreground" title={doc.last_extraction_at ? formatDate(doc.last_extraction_at) : undefined}>
                      {doc.last_extraction_at
                        ? formatRelativeDate(doc.last_extraction_at)
                        : <span className="text-xs">Not run</span>}
                    </td>
                    <td className="p-3">
                      {doc.last_extraction_status ? (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${extractionStatusColor(doc.last_extraction_status)}`}>
                          {doc.last_extraction_status}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Not extracted</span>
                      )}
                    </td>
                    <td className="p-3">
                      {doc.avg_confidence !== null && doc.avg_confidence !== undefined ? (
                        <Badge
                          variant="outline"
                          className={`text-xs ${confidenceColor(doc.avg_confidence)}`}
                        >
                          {(doc.avg_confidence * 100).toFixed(0)}%
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">--</span>
                      )}
                    </td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {(() => {
                          const viewable =
                            doc.file_type === 'pdf' || doc.conversion_status === 'converted'
                          const tooltip = viewable
                            ? doc.conversion_status === 'converted'
                              ? 'View & Annotate (Word converted to PDF)'
                              : 'View & Annotate'
                            : doc.conversion_status === 'failed'
                              ? `Conversion failed: ${doc.conversion_error || 'unknown error'}`
                              : 'View & Annotate (PDF only)'
                          return (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title={tooltip}
                              onClick={() => setViewerDocumentId(doc.id)}
                              disabled={!viewable}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          )
                        })()}
                        {onViewResults && doc.last_extraction_status === 'completed' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="View results"
                            onClick={() => onViewResults(doc.id)}
                          >
                            <BarChart2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            if (confirm('Delete this document?')) deleteMutation.mutate(doc.id)
                          }}
                          title="Delete document"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalDocs > 100 && (
            <div className="flex items-center justify-between px-3 py-2 border-t text-sm">
              <span className="text-muted-foreground">Showing {start + 1}-{end} of {totalDocs}</span>
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
                <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>Prev</Button>
                <span className="text-muted-foreground">Page {safePage} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={safePage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
            </>)
          })()}
        </div>
      )}

      {showProcessingModal && (
        <ProcessingModal
          projectId={projectId}
          documentIds={selectedDocs}
          onClose={handleProcessingClose}
          onJobStarted={(jobId) => setActiveJobId(jobId)}
        />
      )}

      {viewerDocumentId && (
        <DocumentViewerDialog
          open={!!viewerDocumentId}
          onOpenChange={(open) => {
            if (!open) setViewerDocumentId(null)
          }}
          projectId={projectId}
          documentId={viewerDocumentId}
        />
      )}
    </div>
  )
}

function DocStatusBadge({ doc }: { doc: { status: string; conversion_status: string | null; error_message?: string | null; conversion_error?: string | null } }) {
  const status = doc.status || 'pending'
  const label =
    status === 'pending' ? 'Pending'
    : status === 'processing' ? 'Processing'
    : status === 'completed' ? 'Ready'
    : status === 'failed' ? 'Failed'
    : status

  const colorClass =
    status === 'completed' ? 'text-green-700 bg-green-50 border-green-200'
    : status === 'processing' ? 'text-blue-700 bg-blue-50 border-blue-200'
    : status === 'failed' ? 'text-red-700 bg-red-50 border-red-200'
    : 'text-gray-600 bg-gray-50 border-gray-200'

  const title =
    status === 'failed'
      ? doc.error_message || doc.conversion_error || 'Failed'
      : status === 'processing'
        ? 'Converting and parsing in the background...'
        : status === 'pending'
          ? 'Queued for processing'
          : status === 'completed'
            ? 'Ready for extraction'
            : undefined

  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${colorClass}`}
    >
      {(status === 'pending' || status === 'processing') && (
        <Loader2 className="h-3 w-3 animate-spin" />
      )}
      {label}
    </span>
  )
}

function StuckDocsBanner({
  documents,
  projectId,
  queryClient,
}: {
  documents: { status: string }[]
  projectId: string
  queryClient: any
}) {
  const [retrying, setRetrying] = useState(false)
  const [lastResult, setLastResult] = useState<{ requeued: number } | null>(null)

  // "Stuck" = pending or processing for too long, OR failed.
  // We treat pending/processing as potentially stuck only if there are any
  // failed docs alongside — otherwise the user is in a normal upload-in-flight
  // state and we shouldn't show the banner.
  const failedCount = documents.filter((d) => d.status === 'failed').length
  const inFlightCount = documents.filter((d) => d.status === 'pending' || d.status === 'processing').length
  const showBanner = failedCount > 0 || inFlightCount >= 5

  if (!showBanner) return null

  const handleRetry = async () => {
    setRetrying(true)
    setLastResult(null)
    try {
      const res = await documentsApi.retryStuck(projectId)
      setLastResult(res)
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
      setTimeout(() => setLastResult(null), 4000)
    } catch {
      // ignore — user can retry
    } finally {
      setRetrying(false)
    }
  }

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-center gap-3">
      <div className="text-sm flex-1">
        <div className="font-medium text-amber-900">
          {failedCount > 0 && `${failedCount} failed`}
          {failedCount > 0 && inFlightCount > 0 && ', '}
          {inFlightCount > 0 && `${inFlightCount} pending/processing`}
        </div>
        <div className="text-xs text-amber-800 mt-0.5">
          {lastResult
            ? `Re-queued ${lastResult.requeued} document${lastResult.requeued !== 1 ? 's' : ''}. They will reprocess shortly.`
            : 'If documents appear stuck or failed, you can re-queue them for processing.'}
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleRetry}
        disabled={retrying}
        className="text-amber-900 border-amber-300 hover:bg-amber-100"
      >
        {retrying ? (
          <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Re-queuing...</>
        ) : (
          'Retry stuck'
        )}
      </Button>
    </div>
  )
}
